import { parse } from "json2csv";
import { debugLog, getCkHtml } from "./index";

let flattenedData = [];

const resetData = () => {
  flattenedData = [];
};

const hasContent = contentArray => {
  return !!(Array.isArray(contentArray) && contentArray.length);
};

/**
 * Determines the correct parent nested type handle for a block based on its context
 * This improved version properly handles both matrix blocks and composable entries
 *
 * @param {Object} item - The block or entry item
 * @param {String} parentEntryType - Entry type of the parent
 * @param {String} parentEntryTypeHandle - Type handle of parent entry if available
 * @returns {String} Appropriate parent nested type handle
 */
const determineParentNestedTypeHandle = (
  item,
  parentEntryType,
  parentEntryTypeHandle
) => {
  // SPECIAL CASE 1: Matrix blocks within steps
  // Blocks in steps should always have "step_Entry" as parent
  if (
    parentEntryTypeHandle === "step_Entry" ||
    (item.typeHandle && item.typeHandle.includes("step_"))
  ) {
    return "step_Entry";
  }

  // SPECIAL CASE 2: Matrix blocks within content builder
  // Blocks in content builder should have "topicArticleContentBuilder_Entry" as parent
  if (
    parentEntryTypeHandle === "topicArticleContentBuilder_Entry" ||
    (item.typeHandle && item.typeHandle.includes("topicArticleContentBuilder_"))
  ) {
    return "topicArticleContentBuilder_Entry";
  }

  // SPECIAL CASE 3: Blocks within columns should use "columnComposableBlock" as parent
  if (
    parentEntryTypeHandle === "columnComposableBlock" ||
    parentEntryTypeHandle === "columnComposableBlock_Entry"
  ) {
    return "columnComposableBlock";
  }

  // CASE 4: For composable article blocks, use the parent article's type handle
  if (parentEntryType) {
    // If parent is an article type, use its proper type handle
    if (
      [
        "ArticleLongform",
        "ArticleStep",
        "ArticleLongformComposable",
        "ArticleStepByStepComposable",
      ].includes(parentEntryType)
    ) {
      return mapEntryTypeToTypeHandle(parentEntryType);
    }
  }

  // CASE 5: If parentEntryTypeHandle is already in the right format, use that
  if (parentEntryTypeHandle && parentEntryTypeHandle.includes("_Entry")) {
    return parentEntryTypeHandle;
  }

  // If we get here and still have a parentEntryTypeHandle, try to map it
  if (parentEntryTypeHandle) {
    return mapEntryTypeToTypeHandle(parentEntryTypeHandle);
  }

  // Default case - empty string if no relationship can be determined
  return "";
};

/**
 * Maps user-friendly entry types to their correct type handle format
 * This is crucial for the mutation factory to identify relationships
 *
 * @param {String} entryType - The user-friendly entry type (e.g., "ArticleLongform")
 * @returns {String} The correct entry type handle (e.g., "articleLongform_Entry")
 */
const mapEntryTypeToTypeHandle = entryType => {
  const typeMap = {
    // Article types
    ArticleLongform: "articleLongform_Entry",
    ArticleStep: "articleWithSteps_Entry",
    ArticleLongformComposable: "articleLongformComposableEntries_Entry",
    ArticleStepByStepComposable: "articleStepByStepComposableEntries_Entry",

    // Basic entry types
    Manual: "product_Entry",
    Chapter: "chapter_Entry",
    Step: "step_Entry",

    // Block types
    textBlock: "textBlock_Entry",
    textPlusImageBlock: "textPlusImageBlock_Entry",
    calloutBlock: "calloutBlock_Entry",
    videoBlock: "videoBlock_Entry",
    richTextInlineImagesBlock: "richTextInlineImagesBlock_Entry", // not needed as the default _Entry should work but here for completeness
    columnComposableBlock: "columnComposableBlock_Entry",
  };

  return typeMap[entryType] || `${entryType}_Entry`;
};

/**
 * Extracts content from a block and adds it to the flattened data structure
 * Fixed to correctly set parentNestedEntryTypeHandle values
 * Enhanced to include export timestamp
 *
 * @param {Array|Object} content - The content block(s) to process
 * @param {String} entryParentId - ID of the parent entry containing this content
 * @param {String} parentEntryType - Entry type of the parent
 * @param {String} parentEntryTypeHandle - Type handle of the parent
 * @param {String} stepBuilderParent - ID of step builder parent (if applicable)
 * @param {String} stepBuilderSortOrder - Sort order within step builder (if applicable)
 * @param {String} sectionHandle - Section handle of the content
 * @param {String} overrideParentTypeHandle - Override the parent type handle (optional)
 * @param {String} exportTimestamp - Timestamp when this export was created
 */
const extractContent = (
  content,
  entryParentId,
  parentEntryType,
  parentEntryTypeHandle,
  stepBuilderParent,
  stepBuilderSortOrder,
  sectionHandle,
  overrideParentTypeHandle = null,
  exportTimestamp = null,
  includeImageBlocks
) => {
  if (!content) return;

  const contentArray = Array.isArray(content) ? content : [content];

  debugLog(
    `extractContent: Processing ${contentArray.length} blocks for parent ID ${entryParentId}, parentType=${parentEntryType}, parentTypeHandle=${parentEntryTypeHandle}`
  );

  contentArray.forEach(item => {
    // Skip if item is not valid
    if (!item) return;

    // Skip image blocks from export - they can't be translated
    if (
      !includeImageBlocks &&
      (item.typeHandle === "largeImageBlock" ||
        item.typeHandle === "fullWidthImageBlock")
    ) {
      return;
    }

    // Determine the effective parent nested type handle
    // If override is provided, use that; otherwise determine based on context
    const effectiveParentNestedTypeHandle =
      overrideParentTypeHandle ||
      determineParentNestedTypeHandle(
        item,
        parentEntryType,
        parentEntryTypeHandle
      );

    debugLog(
      `Block ${item.id} (${item.typeHandle}) has parent relationship: ${effectiveParentNestedTypeHandle}`
    );

    // Base row data common to all content types
    const row = {
      entryType: item.typeHandle,
      entryId: item.id,
      entryUserTitle: "", // Will be populated for specific entry types
      entryContent: "", // Default empty string, will be set based on type
      entryTypeHandle: item.__typename || item.typeHandle,
      sortOrder: item.sortOrder ? item.sortOrder.toString() : "",
      entryParentId,
      stepBuilderParent,
      stepBuilderSortOrder: stepBuilderSortOrder
        ? stepBuilderSortOrder.toString()
        : "",
      imageUrl: item.image?.[0]?.url || null,
      youtubeVideo: item.youtubeVideo || "",
      sectionHandle: item.sectionHandle || sectionHandle || "",
      parentNestedEntryTypeHandle: effectiveParentNestedTypeHandle,
      // NEW: Add export timestamp to each row
      exportTimestamp: exportTimestamp || new Date().toISOString(),
      translationImportTimestamp: item.translationImportTimestamp || "",
      contentDateUpdatedPrimarySite:
        "" /* NEW – will be filled later by MissingEntries query */,
    };

    // Handle different block types (content type specific processing)
    switch (item.typeHandle) {
      case "calloutBlock":
        debugLog(`row item ${item.typeHandle}`, item);
        flattenedData.push({
          ...row,
          calloutType: item.calloutType || "",
          entryContent: item.calloutText || "",
        });
        break;

      case "videoBlock":
        flattenedData.push({
          ...row,
          entryContent: getCkHtml(item.paragraph) || "",
          youtubeVideo: item.youtubeVideo || "",
        });
        break;
      /* ───────── rich-text (CKEditor) block ───────── */
      case "richTextInlineImagesBlock":
        flattenedData.push({
          ...row,
          entryContent: getCkHtml(item.richText) || "", // ⬅️  use richText
        });
        break;
      case "columnComposableBlock":
        debugLog(`row item ${item.typeHandle}`, item);
        // First add the column block itself
        flattenedData.push({
          ...row,
          calloutType: "",
        });

        // Then process the entries inside the column
        if (
          item.columnBlockComposableEntries &&
          Array.isArray(item.columnBlockComposableEntries) &&
          item.columnBlockComposableEntries.length > 0
        ) {
          // Important: Pass the column block as the parent type for nested blocks
          extractContent(
            item.columnBlockComposableEntries,
            entryParentId,
            "columnComposableBlock", // Pass the parent type
            "columnComposableBlock", // This becomes parentEntryTypeHandle
            stepBuilderParent,
            stepBuilderSortOrder,
            sectionHandle,
            "columnComposableBlock_Entry", // Override with the correct relationship
            exportTimestamp, // Pass export timestamp through
            includeImageBlocks
          );
        }
        break;
      case "glossaryTerm":
        debugLog(`row item ${item.typeHandle}`, item);
        flattenedData.push({
          ...row,
          entryContent: getCkHtml(item.definition) || "", // ← raw HTML
          entryUserTitle: item.title || "",
        });
        break;
      case "variable":
        debugLog(`row item ${item.typeHandle}`, item);
        flattenedData.push({
          ...row,
          entryContent: item.plainText || "", // ← raw HTML
        });
        break;
      default:
        debugLog("row item default:", item);
        // Standard text block or other content types
        flattenedData.push({
          ...row,
          entryContent: getCkHtml(item.paragraph) || "",
          imageUrl: item.image?.[0]?.url || null,
          calloutType: "",
        });
        break;
    }
  });
};

/**
 * Processes a topic article and its content
 * Fixed to correctly handle parentNestedEntryTypeHandle values and export nested content in composable steps
 * Enhanced to include export timestamp
 *
 * @param {Object} topic - The topic article to process
 * @param {String} sectionHandle - Section handle of the topic
 * @param {String} exportTimestamp - Timestamp when this export was created
 */
const addTopicContent = (
  topic,
  sectionHandle,
  exportTimestamp = null,
  includeImageBlocks
) => {
  if (!topic) return;

  // Get the section handle from the topic if available
  const topicSectionHandle = topic.sectionHandle || sectionHandle || "";

  // Determine the article type and its proper type handle
  let topicEntryType;
  let topicTypeHandle;

  if (hasContent(topic.topicArticleStepBuilder)) {
    topicEntryType = "ArticleStep";
    topicTypeHandle = "articleWithSteps_Entry";
  } else if (hasContent(topic.topicArticleContentBuilder)) {
    topicEntryType = "ArticleLongform";
    topicTypeHandle = "articleLongform_Entry";
  } else if (topic.topicArticleContentBuilderComposableEntries) {
    topicEntryType = "ArticleLongformComposable";
    topicTypeHandle = "articleLongformComposableEntries_Entry";
  } else if (topic.stepArticlesComposableEntries) {
    topicEntryType = "ArticleStepByStepComposable";
    topicTypeHandle = "articleStepByStepComposableEntries_Entry";
  } else {
    topicEntryType = topic.typeHandle || "Unknown";
    topicTypeHandle = mapEntryTypeToTypeHandle(topicEntryType);
  }

  debugLog(
    `Processing topic: id=${topic.id}, type=${topicEntryType}, typeHandle=${topicTypeHandle}`
  );

  // Add the topic entry itself
  flattenedData.push({
    entryType: topicEntryType,
    entryId: topic.id,
    entryUserTitle: topic.userTitle || "",
    entryContent: "",
    entryTypeHandle: topicTypeHandle, // Use the mapped type handle
    entryParentId: null,
    calloutType: "",
    sortOrder: topic.sortOrder || "",
    sectionHandle: topicSectionHandle,
    parentNestedEntryTypeHandle: "",
    stepBuilderParent: "",
    stepBuilderSortOrder: "",
    youtubeVideo: "",
    // NEW: Add export timestamp
    exportTimestamp: exportTimestamp || new Date().toISOString(),
    exportDate: exportTimestamp
      ? new Date(exportTimestamp).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
  });

  // CASE 1: Step-based article with matrix blocks
  if (hasContent(topic.topicArticleStepBuilder)) {
    debugLog(
      `Processing Step-based article: ${topic.topicArticleStepBuilder.length} steps found`
    );

    topic.topicArticleStepBuilder.forEach((singleStep, stepIndex) => {
      // Add step row
      flattenedData.push({
        entryType: "Step",
        entryId: singleStep.id,
        entryUserTitle: `Step ${stepIndex + 1}`,
        entryContent: "",
        entryTypeHandle: "step_Entry", // Correct format
        sortOrder: singleStep.sortOrder || stepIndex.toString(),
        entryParentId: topic.id,
        calloutType: "",
        sectionHandle: topicSectionHandle,
        parentNestedEntryTypeHandle: topicTypeHandle, // Parent is the article
        stepBuilderParent: singleStep.id,
        stepBuilderSortOrder: singleStep.sortOrder || stepIndex.toString(),
        imageUrl: singleStep.image?.[0]?.url || null,
        youtubeVideo: "",
        // NEW: Add export timestamp
        exportTimestamp: exportTimestamp || new Date().toISOString(),
        exportDate: exportTimestamp
          ? new Date(exportTimestamp).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });

      // Extract content within step, using "step_Entry" as parentNestedEntryTypeHandle
      extractContent(
        singleStep.step,
        topic.id,
        "Step",
        "step_Entry", // This is crucially important
        singleStep.id,
        singleStep.sortOrder,
        topicSectionHandle,
        "step_Entry", // Override with correct relationship
        exportTimestamp, // Pass export timestamp through
        includeImageBlocks
      );
    });
  }

  // CASE 2: Longform article with matrix blocks
  else if (hasContent(topic.topicArticleContentBuilder)) {
    debugLog(
      `Processing Longform matrix-based article with ${topic.topicArticleContentBuilder.length} content blocks`
    );

    // Extract content with "topicArticleContentBuilder_Entry" as parentNestedEntryTypeHandle
    extractContent(
      topic.topicArticleContentBuilder,
      topic.id,
      topicEntryType,
      "topicArticleContentBuilder",
      null,
      null,
      topicSectionHandle,
      "topicArticleContentBuilder_Entry", // Override with correct relationship
      exportTimestamp // Pass export timestamp through
    );
  }

  // CASE 3: Longform composable article with separate entries
  else if (hasContent(topic.topicArticleContentBuilderComposableEntries)) {
    debugLog(
      `Processing Longform composable article with ${topic.topicArticleContentBuilderComposableEntries.length} composable entries`
    );

    topic.topicArticleContentBuilderComposableEntries.forEach(entry => {
      debugLog("longform composable sub entry", entry);
      if (!entry) return;

      // Add each composable entry
      flattenedData.push({
        entryType: entry.typeHandle,
        entryId: entry.id,
        entryUserTitle: entry.userTitle || "",
        entryContent: getCkHtml(entry.paragraph) || entry.calloutText || "",
        entryTypeHandle: entry.__typename || entry.typeHandle,
        entryParentId: topic.id,
        calloutType: entry.calloutType || "",
        sortOrder: entry.sortOrder || "",
        sectionHandle: entry.sectionHandle || topicSectionHandle,
        parentNestedEntryTypeHandle: topicTypeHandle, // Parent is the article (important!)
        stepBuilderParent: "",
        stepBuilderSortOrder: "",
        imageUrl: entry.image?.[0]?.url || null,
        youtubeVideo: entry.youtubeVideo || "",
        // NEW: Add export timestamp
        exportTimestamp: exportTimestamp || new Date().toISOString(),
        exportDate: exportTimestamp
          ? new Date(exportTimestamp).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });

      // Handle nested content in composable entries
      if (
        entry.typeHandle === "columnComposableBlock" &&
        hasContent(entry.columnBlockComposableEntries)
      ) {
        debugLog(
          "columnComposableBlock entry inside longform composable entry.columnBlockComposableEntries",
          entry.columnBlockComposableEntries
        );
        extractContent(
          entry.columnBlockComposableEntries,
          topic.id,
          entry.typeHandle,
          "columnComposableBlock",
          null,
          null,
          entry.sectionHandle || topicSectionHandle,
          "columnComposableBlock", // Consistent format for column blocks
          exportTimestamp, // Pass export timestamp through
          includeImageBlocks
        );
      }
    });
  }

  // CASE 4: Step-by-step composable article with separate entries
  else if (hasContent(topic.stepArticlesComposableEntries)) {
    debugLog(
      `Processing Step-by-step composable article with ${topic.stepArticlesComposableEntries.length} step entries`
    );

    topic.stepArticlesComposableEntries.forEach((stepEntry, index) => {
      debugLog("stepComposable sub entry", stepEntry);
      if (!stepEntry) return;

      /* 
      //remove this entire composable step row for now as the row below it will still have the articleLongform or articleLongformComposableEntries row with the same ID
      
      // Add step row
      flattenedData.push({
        entryType: "Step",
        entryId: stepEntry.id,
        entryUserTitle: `Step ${index + 1}`,
        entryContent: "",
        entryTypeHandle: "step_Entry",
        sortOrder: stepEntry.sortOrder || index.toString(),
        entryParentId: topic.id,
        calloutType: "",
        sectionHandle: topicSectionHandle,
        parentNestedEntryTypeHandle: topicTypeHandle, // Parent is the article
        stepBuilderParent: stepEntry.id,
        stepBuilderSortOrder: stepEntry.sortOrder || index.toString(),
        youtubeVideo: "",
        // NEW: Add export timestamp
        exportTimestamp: exportTimestamp || new Date().toISOString(),
        exportDate: exportTimestamp ? new Date(exportTimestamp).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });
      */

      // Determine the step entry type and handle
      const stepEntryTypeHandle = stepEntry.__typename || stepEntry.typeHandle;
      const stepSectionHandle = stepEntry.sectionHandle || topicSectionHandle;

      // Add step entry (the actual article sub entry within the step)
      flattenedData.push({
        entryType: stepEntry.typeHandle,
        entryId: stepEntry.id,
        entryUserTitle: stepEntry.userTitle || "",
        entryContent:
          getCkHtml(stepEntry.paragraph) || stepEntry.calloutText || "",
        entryTypeHandle: stepEntryTypeHandle,
        entryParentId: topic.id,
        calloutType: stepEntry.calloutType || "",
        sortOrder: stepEntry.sortOrder || index.toString(),
        sectionHandle: stepSectionHandle,
        parentNestedEntryTypeHandle: topicTypeHandle, // Parent is the article
        stepBuilderParent: stepEntry.id,
        stepBuilderSortOrder: stepEntry.sortOrder || index.toString(),
        imageUrl: stepEntry.image?.[0]?.url || null,
        youtubeVideo: stepEntry.youtubeVideo || "",
        // NEW: Add export timestamp
        exportTimestamp: exportTimestamp || new Date().toISOString(),
        translationImportTimestamp: stepEntry.translationImportTimestamp || "",
      });

      // IMPORTANT: Process the content within the step entry
      // Check if the step entry is an articleLongform with matrix blocks
      if (
        stepEntry.typeHandle === "articleLongform" &&
        hasContent(stepEntry.topicArticleContentBuilder)
      ) {
        debugLog(
          `Processing content blocks within step entry ${stepEntry.id} (articleLongform)`
        );

        extractContent(
          stepEntry.topicArticleContentBuilder,
          topic.id, // Parent is still the main topic
          stepEntry.typeHandle,
          "topicArticleContentBuilder",
          stepEntry.id, // Step builder parent is the step entry itself
          stepEntry.sortOrder || index.toString(),
          stepSectionHandle,
          "topicArticleContentBuilder_Entry", // Override with correct relationship
          exportTimestamp, // Pass export timestamp through
          includeImageBlocks
        );
      }

      // Check if the step entry is an articleLongformComposableEntries with composable blocks
      else if (
        stepEntry.typeHandle === "articleLongformComposableEntries" &&
        hasContent(stepEntry.topicArticleContentBuilderComposableEntries)
      ) {
        debugLog(
          `Processing composable blocks within step entry ${stepEntry.id} (articleLongformComposableEntries)`
        );

        stepEntry.topicArticleContentBuilderComposableEntries.forEach(
          composableEntry => {
            if (!composableEntry) return;

            // Add each composable entry within the step
            flattenedData.push({
              entryType: composableEntry.typeHandle,
              entryId: composableEntry.id,
              entryUserTitle: composableEntry.userTitle || "",
              entryContent:
                getCkHtml(composableEntry.paragraph) ||
                composableEntry.calloutText ||
                "",
              entryTypeHandle:
                composableEntry.__typename || composableEntry.typeHandle,
              entryParentId: topic.id,
              calloutType: composableEntry.calloutType || "",
              sortOrder: composableEntry.sortOrder || "",
              sectionHandle: composableEntry.sectionHandle || stepSectionHandle,
              parentNestedEntryTypeHandle: stepEntryTypeHandle, // Parent is the step entry
              stepBuilderParent: stepEntry.id,
              stepBuilderSortOrder: stepEntry.sortOrder || index.toString(),
              imageUrl: stepEntry.image?.[0]?.url || null,
              youtubeVideo: composableEntry.youtubeVideo || "",
              // NEW: Add export timestamp
              exportTimestamp: exportTimestamp || new Date().toISOString(),
              translationImportTimestamp:
                composableEntry.translationImportTimestamp || "",
            });

            // Handle nested content in composable entries (like column blocks)
            if (
              composableEntry.typeHandle === "columnComposableBlock" &&
              hasContent(composableEntry.columnBlockComposableEntries)
            ) {
              extractContent(
                composableEntry.columnBlockComposableEntries,
                topic.id,
                composableEntry.typeHandle,
                "columnComposableBlock",
                stepEntry.id, // Step builder parent remains the step entry
                stepEntry.sortOrder || index.toString(),
                composableEntry.sectionHandle || stepSectionHandle,
                "columnComposableBlock", // Consistent format for column blocks
                exportTimestamp, // Pass export timestamp through
                includeImageBlocks,
              );
            }
          }
        );
      }

      // Handle any other nested content patterns that might exist
      else if (stepEntry.content && hasContent(stepEntry.content)) {
        debugLog(
          `Processing generic content within step entry ${stepEntry.id}`
        );

        extractContent(
          stepEntry.content,
          topic.id,
          stepEntry.typeHandle,
          "step_Entry",
          stepEntry.id,
          stepEntry.sortOrder || index.toString(),
          stepSectionHandle,
          "step_Entry", // Correct parent relationship for blocks in steps
          exportTimestamp, // Pass export timestamp through
          includeImageBlocks
        );
      }
    });
  }
};

/**
 * Builds a structured representation of the manual for export
 * This is the main function that drives the export process
 * Enhanced to include export timestamp
 *
 * @param {Object} data - The manual data to export
 * @returns {Array} Flattened data structure for CSV export
 */
export const buildExportStructure = (data, includeImageBlocks = false) => {
  // Important. Clear any data being stored from a prior export.
  resetData();

  if (!data) {
    console.error("No data provided to buildExportStructure");
    return [];
  }

  // Create export timestamp for this export session
  const exportTimestamp = new Date().toISOString();

  debugLog(
    `Starting manual export: id=${data.id}, title=${data.userTitle ||
      "No title"}, sectionHandle=${data.sectionHandle ||
      "none"}, exportTimestamp=${exportTimestamp}`
  );

  // Get the section handle from the manual if available
  const manualSectionHandle = data.sectionHandle || "";

  // Add first entry to the flattened array, containing the "manual" entryType for the overall manual
  flattenedData.push({
    entryType: "Manual",
    entryId: data.id,
    entryUserTitle: data.userTitle || "",
    entryContent: "",
    entryTypeHandle: data.__typename || data.typeHandle || "product_Entry",
    entryParentId: null,
    calloutType: "",
    sortOrder: "",
    sectionHandle: manualSectionHandle,
    parentNestedEntryTypeHandle: "",
    stepBuilderParent: "",
    stepBuilderSortOrder: "",
    youtubeVideo: "",
    // NEW: Add export timestamp
    exportTimestamp,
    translationImportTimestamp: data.translationImportTimestamp || "",
  });

  // Process all chapters in the manual
  if (
    Object.prototype.hasOwnProperty.call(data, "children") &&
    Array.isArray(data.children)
  ) {
    debugLog(`Processing ${data.children.length} chapters`);

    data.children.forEach((chapter, chapterIndex) => {
      if (!chapter) return;

      // Get chapter section handle
      const chapterSectionHandle = chapter.sectionHandle || manualSectionHandle;

      debugLog(
        `Processing chapter ${chapterIndex + 1}: id=${
          chapter.id
        }, title=${chapter.userTitle || "No title"}`
      );

      // Add chapter entry
      flattenedData.push({
        entryType: "Chapter",
        entryId: chapter.id,
        entryUserTitle: chapter.userTitle || "",
        entryContent: "",
        entryTypeHandle:
          chapter.__typename || chapter.typeHandle || "chapter_Entry",
        entryParentId: data.id, // Set manual as the parent
        calloutType: "",
        sortOrder: chapter.sortOrder || chapterIndex.toString(),
        sectionHandle: chapterSectionHandle,
        parentNestedEntryTypeHandle:
          data.__typename || data.typeHandle || "product_Entry", // Adding manual as parent
        stepBuilderParent: "",
        stepBuilderSortOrder: "",
        youtubeVideo: "",
        // NEW: Add export timestamp
        exportTimestamp,
        translationImportTimestamp: chapter.translationImportTimestamp || "",
      });

      // Add topic data. May include step-by-step or longform articles.
      if (
        Object.prototype.hasOwnProperty.call(chapter, "topicArticles") &&
        Array.isArray(chapter.topicArticles)
      ) {
        debugLog(
          `Processing ${chapter.topicArticles.length} topics in chapter ${chapter.id}`
        );

        chapter.topicArticles.forEach(topic => {
          if (!topic) return;
          addTopicContent(topic, chapterSectionHandle, exportTimestamp);
        });
      }
    });
  }

  debugLog(
    `Export complete. ${flattenedData.length} rows generated with export timestamp ${exportTimestamp}.`
  );

  return flattenedData;
};

/**
 * Builds a CSV export from CraftCMS data
 *
 * @param {Object} data - The manual data from CraftCMS
 * @returns {String} CSV string or error
 */
const buildExportCsv = data => {
  try {
    debugLog("Starting CSV export process");

    const exportData = buildExportStructure(data);

    if (exportData.length === 0) {
      console.error("No data generated for CSV export");
      return "Error: No data available for export";
    }

    debugLog(`Generated ${exportData.length} rows for CSV export`);

    // Generate CSV
    const csv = parse(exportData, {
      flatten: true,
      unwind: true,
    });

    // Quick validation check for calloutBlock content
    const calloutBlocks = exportData.filter(
      row => row.entryType === "calloutBlock"
    );
    if (calloutBlocks.length > 0) {
      const emptyCalloutContent = calloutBlocks.filter(
        block => !block.entryContent
      ).length;
      debugLog(
        `CSV contains ${calloutBlocks.length} callout blocks. Empty content: ${emptyCalloutContent}`
      );
    }

    // Log export timestamp info
    const uniqueTimestamps = [
      ...new Set(exportData.map(row => row.exportTimestamp)),
    ];
    debugLog(
      `Export completed with ${
        uniqueTimestamps.length
      } unique timestamp(s): ${uniqueTimestamps.join(", ")}`
    );

    return csv;
  } catch (err) {
    console.error("Error generating CSV export:", err);
    return `Error generating CSV: ${err.message}`;
  }
};

export default buildExportCsv;
